import _ from 'lodash';
// reselect is included by @reduxjs/toolkit, it's not in the package.json because we would have to specify the version
// but we should always be using the version required by RTK.
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  createSelectorCreator,
  lruMemoize,
  createSelector
} from 'reselect';
import { notifySuccess, notifyError } from 'common/state/notificationState';
import { csrfFromDom } from 'common/utils/server';
import access from '../access';
import api from '../api';

const SECTIONS_WEIGHT_PERCENTAGE = 100;

// Selectors
// create a "selector creator" that uses lodash.isEqual instead of ===
const createDeepEqualSelector = createSelectorCreator(lruMemoize, _.isEqual);

export const getFormStatus = (builder, forms) => {
  const { formValues } = builder;
  const savedForm = _.find(forms.forms, { id: formValues.id });

  if (!savedForm) return 'new';
  if (!savedForm.editable) return 'not-editable';
  if (_.isEqual(formValues, savedForm)) return 'up-to-date';

  return 'unsaved-changes';
};

export const getFormValues = state => state.qaBuilder.formValues;
export const getSections = state => state.qaBuilder.formValues.sections;
export const getNonDefaultSections = createDeepEqualSelector(
  getSections,
  sections => sections.filter(section => !section.default_section)
);
export const getScoresBySection = createDeepEqualSelector(
  getSections,
  sections =>
    sections.map(section => {
      let maxSectionScore = 0;
      section.questions.forEach(question => {
        const optionScores = question.options.map(option =>
          parseFloat(option.score || 0)
        );
        const maxQuestionScore = Math.max(...optionScores);
        maxSectionScore += maxQuestionScore;
      });
      return {
        score: maxSectionScore,
        title: section.title,
        weight: section.weight,
      };
    })
);
export const getTotalScore = createSelector(getScoresBySection, sectionScores =>
  sectionScores.map(p => p.score).reduce((a, b) => a + b, 0)
);
export const getSectionsWeightTotal = createDeepEqualSelector(
  getSections,
  sections => _.sumBy(sections, section => parseFloat(section.weight) || 0)
);
export const getWeightedSections = state =>
  state.qaBuilder.formValues.weighted_sections;

// Actions

const APPEND_AT_PATH = 'builder/APPEND_AT_PATH';
const PREPEND_AT_PATH = 'builder/PREPEND_AT_PATH';
const SET_AT_PATH = 'builder/SET_AT_PATH';
const UPDATE_AT_PATH = 'builder/UPDATE_AT_PATH';
const REMOVE_AT_PATH = 'builder/REMOVE_AT_PATH';
const SET_QA_FORMS = 'builder/SET_QA_FORMS';
const SET_ACTIVE = 'builder/SET_ACTIVE';
const NEW_QA_FORM = 'builder/NEW_QA_FORM';
const SET_LOADING = 'builder/SET_LOADING';

// Defailt definintions
const ts = ~~(Date.now() / 1000);

export const initialOptionState = () => ({
  id: _.uniqueId(`option_${ts}-`),
  text: '',
  score: 0,
  autofail: false,
});

export const initialQuestionState = () => ({
  id: _.uniqueId(`question_${ts}-`),
  text: '',
  instructions: '',
  options: [initialOptionState()],
});

export const initialSectionState = (defaultSection = true) => ({
  id: _.uniqueId(`section_${ts}-`),
  title: '',
  default_section: defaultSection,
  weight: 0,
  questions: [initialQuestionState()],
});

const initialState = () => ({
  loading: false,
  qaForms: [],
  formValues: {
    type: 'regular',
    name: '',
    id: _.uniqueId(`form_${ts}-`),
    isNew: true,
    sections: [initialSectionState()],
  },
});

// Action Creators

export const appendAtPath = (path, valueFunc) => ({
  type: APPEND_AT_PATH,
  path,
  valueFunc,
});
export const prependAtPath = (path, valueFunc) => ({
  type: PREPEND_AT_PATH,
  path,
  valueFunc,
});
export const setAtPath = (path, value) => ({ type: SET_AT_PATH, path, value });
export const updateAtPath = (path, func) => ({
  type: UPDATE_AT_PATH,
  path,
  func,
});
export const removeAtPath = path => ({ type: REMOVE_AT_PATH, path });
export const setQAForms = qaForms => ({ type: SET_QA_FORMS, qaForms });
export const setActive = id => ({ type: SET_ACTIVE, id });
export const newQAForm = () => ({ type: NEW_QA_FORM });
export const setLoading = loading => ({ type: SET_LOADING, loading });

export const distributeWeightOnSections = () => (dispatch, getState) => {
  const sections = getSections(getState());
  const weight = Math.round((1 / sections.length) * 100);

  sections.forEach(section => {
    dispatch(
      setAtPath(
        ['sections', access.where(_.matches({ id: section.id })), 'weight'],
        weight
      )
    );
  });
};

export const fetchAndSetQAForm = formId => dispatch => {
  dispatch(setLoading(true));

  if (formId === 'new') {
    dispatch(setAtPath([], initialState().formValues));
    return dispatch(setLoading(false));
  }

  return api.fetch(dispatch, `qa_forms/${formId}`).then(([json]) => {
    dispatch(setAtPath([], json));
    dispatch(setLoading(false));
  });
};

function validate(values) {
  const errors = [];

  if (!values.name.length) {
    errors.push('Scorecard name is required.');
  }

  const totalWeight = _.sumBy(
    values.sections,
    section => parseFloat(section.weight) || 0
  );
  const isWeighted = values.weighted_sections;

  if (isWeighted && totalWeight !== SECTIONS_WEIGHT_PERCENTAGE) {
    errors.push(
      'Weights are not distributed correctly. Please check weights before saving.'
    );
  }

  values.sections.forEach((section, sIndex) => {
    if (!values.sections.filter(s => s.default_section).length) {
      if (!section.title.length) {
        errors.push(`Section ${sIndex + 1} title is required`);
      }
    }

    if (!section.questions.length) {
      errors.push(`Section ${sIndex + 1} must have at least one question.`);
    }

    section.questions.forEach((question, qIndex) => {
      if (!question.text.length) {
        errors.push(`Question ${qIndex + 1} text is required.`);
      }

      if (!question.options.length) {
        errors.push(`Question ${qIndex + 1} must have at least one answer.`);
      }

      question.options.forEach((option, oIndex) => {
        if (!option.text.length) {
          errors.push(
            `Question ${qIndex + 1} - Answer ${oIndex + 1} text is required.`
          );
        }

        if (Number.isNaN(parseFloat(option.score))) {
          errors.push(
            `Question ${qIndex + 1} - Answer ${
              oIndex + 1
            } score is required and must be a number.`
          );
        }
      });
    });
  });

  return errors;
}

export const saveRubric = () => (dispatch, getState) => {
  const {
    qaBuilder: { formValues, qaForms },
  } = getState();
  const url = formValues.isNew
    ? '/api/v1/qa_forms'
    : `/api/v1/qa_forms/${formValues.id}`;

  const errors = validate(formValues);
  if (errors.length) {
    return dispatch(notifyError(errors[0]));
  }

  return fetch(url, {
    method: formValues.isNew ? 'post' : 'put',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-token': csrfFromDom(),
    },
    body: JSON.stringify(formValues),
  })
    .then(result => {
      if (!result.ok) {
        throw {
          body: result.json(),
          status: result.status,
          message: result.statusText,
        };
      }
      return result.json();
    })
    .then(json => {
      dispatch(setAtPath([], json));
      dispatch(notifySuccess('Scorecard saved!'));

      if (formValues.isNew) {
        dispatch(setQAForms([json, ...qaForms]));
        window.location.replace('/admin/company/qa_settings');
      } else {
        const forms = qaForms.map(f => (f.id === json.id ? json : f));
        dispatch(setQAForms(forms));
      }
    })
    .catch(e => {
      if (e.status === 422) {
        e.body.then(json => {
          dispatch(notifyError(JSON.stringify(json)));
        });
      } else {
        dispatch(notifyError(e.message));
      }
    });
};

export const addSection = position => (dispatch, getState) => {
  const {
    qaBuilder: {
      formValues: { sections },
    },
  } = getState();

  if (sections.filter(section => section.default_section).length === 1) {
    const firstSection = sections[0];
    const path = ['sections', access.where(_.matches({ id: firstSection.id }))];
    return dispatch(
      updateAtPath(path, section => ({ ...section, default_section: false }))
    );
  }

  if (position === 'after') {
    return dispatch(
      appendAtPath(['sections'], () => initialSectionState(false))
    );
  }

  return dispatch(
    prependAtPath(['sections'], () => initialSectionState(false))
  );
};

export const removeSection = path => (dispatch, getState) => {
  const {
    qaBuilder: { formValues },
  } = getState();

  if (formValues.sections.length === 1) {
    return dispatch(
      updateAtPath(path, section => ({
        ...section,
        title: '',
        default_section: true,
      }))
    );
  }

  return dispatch(removeAtPath(path));
};

// Reducer

export const reducer = (state = initialState(), action) => {
  switch (action.type) {
    case APPEND_AT_PATH: {
      const { path, valueFunc } = action;
      const { formValues } = state;

      return {
        ...state,
        formValues: access.updateBy(formValues, path, a => [...a, valueFunc()]),
      };
    }

    case PREPEND_AT_PATH: {
      const { path, valueFunc } = action;
      const { formValues } = state;

      return {
        ...state,
        formValues: access.updateBy(formValues, path, a => [valueFunc(), ...a]),
      };
    }

    case SET_LOADING: {
      return {
        ...state,
        loading: action.loading,
      };
    }

    case SET_AT_PATH: {
      const { path, value } = action;
      const { formValues } = state;

      return {
        ...state,
        formValues: access.updateBy(formValues, path, () => value),
      };
    }

    case UPDATE_AT_PATH: {
      const { formValues } = state;

      return {
        ...state,
        formValues: access.updateBy(formValues, action.path, action.func),
      };
    }

    case REMOVE_AT_PATH: {
      const { path } = action;
      const { formValues } = state;

      return {
        ...state,
        formValues: access.deleteBy(formValues, path),
      };
    }

    case SET_QA_FORMS: {
      const { qaForms } = action;

      return { ...state, qaForms };
    }

    case SET_ACTIVE: {
      const { id } = action;

      const formValues = state.qaForms.find(q => q.id == id);

      return { ...state, formValues };
    }

    case NEW_QA_FORM: {
      return { ...state, formValues: initialState().formValues };
    }

    default:
      return state;
  }
};
